import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  Dialog,
  DialogTitle
} from '@mui/material';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import CloseIcon from '@mui/icons-material/Close';
import HeaderPublic from '../../components/HeaderPublic';
import Footer from '../../components/Footer';
import ProfileView from '../profileDetails/ProfileView';
import EmailOTPValidation from '../../components/EmailOTPValidation';
import { getPublicList } from '../../services/userService';

const PublicView = () => {
  const { listId } = useParams();
  const [list, setList] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [validationDialogOpen, setValidationDialogOpen] = useState(false);
  const [isValidated, setIsValidated] = useState(false);

  // Check if user is already validated for this list
  useEffect(() => {
    const checkValidation = () => {
      const validationData = localStorage.getItem('publicViewValidated');
      console.log('Validation data from localStorage:', validationData);
      
      if (validationData) {
        try {
          const parsed = JSON.parse(validationData);
          const currentListId = window.location.pathname.split('/').pop();
          console.log('Current listId:', currentListId, 'Stored listId:', parsed.listId);
          
          // Check if validation is for current list and not expired (24 hours)
          if (parsed.listId === currentListId && 
              (Date.now() - parsed.timestamp) < 24 * 60 * 60 * 1000) {
            console.log('User is already validated for this list');
            setIsValidated(true);
            setError(null); // Clear any existing error
            return true;
          } else {
            console.log('Validation expired or for different list');
            // Remove expired validation
            localStorage.removeItem('publicViewValidated');
          }
        } catch (error) {
          console.error('Error parsing validation data:', error);
          localStorage.removeItem('publicViewValidated');
        }
      }
      return false;
    };

    if (listId) {
      console.log('Checking validation for listId:', listId);
      const isAlreadyValidated = checkValidation();
      if (!isAlreadyValidated) {
        console.log('Opening validation dialog');
        setValidationDialogOpen(true);
      }
    }
  }, [listId]);

  useEffect(() => {
    const fetchPublicList = async () => {
      try {
        setLoading(true);
        const response = await getPublicList(listId);
        
        if (response.status === 'success') {
          console.log('Public list fetched successfully:', response.data);
          setList(response.data);
          
          // Use profiles data from backend response
          if (response.data.profiles && response.data.profiles.length > 0) {
            setProfiles(response.data.profiles);
          } else {
            setProfiles([]);
          }
        }
      } catch (error) {
        console.error('Error fetching public list:', error);
        setError('Failed to load list. The link may be invalid or expired.');
      } finally {
        setLoading(false);
      }
    };

    if (listId && isValidated) {
      fetchPublicList();
    }
  }, [listId, isValidated]);

  // Profile click handler
  const handleProfileClick = (profile) => {
    setSelectedProfile(profile);
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setSelectedProfile(null);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedProfile(null);
  };

  const handleValidationSuccess = () => {
    console.log('Validation success called');
    setIsValidated(true);
    setValidationDialogOpen(false);
    setError(null); // Clear any existing error
  };

  const handleValidationClose = () => {
    console.log('handleValidationClose called, isValidated:', isValidated);
    setValidationDialogOpen(false);
    // Only set error if user is not validated
    if (!isValidated) {
      console.log('Setting error because user is not validated');
      setError('Email verification is required to view this list.');
    } else {
      console.log('Not setting error because user is validated');
    }
  };

  // Debug logging
  console.log('Render state:', { 
    isValidated, 
    validationDialogOpen, 
    loading, 
    error,
    listId,
    list: !!list,
    profiles: profiles.length
  });
  
  // Additional debugging for error state
  if (error) {
    console.log('Error state is set:', error);
    console.trace('Error was set from:');
  }

  // Show validation dialog if not validated and dialog is not open
  if (!isValidated && !validationDialogOpen) {
    console.log('Showing email verification required message');
    return (
      <Box sx={{ bgcolor: '#fff', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <HeaderPublic />
        <Container maxWidth="md" sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Alert severity="info">
            Email verification is required to view this list.
          </Alert>
        </Container>
        <Footer />
      </Box>
    );
  }

  // Show loading screen while validation dialog is open but not validated
  if (!isValidated && validationDialogOpen) {
    return (
      <Box sx={{ bgcolor: '#fff', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <HeaderPublic />
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '50vh',
          flex: 1
        }}>
          <CircularProgress sx={{ color: '#1BE885' }} />
        </Box>
        <Footer />
        
        {/* Email OTP Validation Dialog */}
        <EmailOTPValidation
          open={validationDialogOpen}
          onClose={handleValidationClose}
          onValidationSuccess={handleValidationSuccess}
        />
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ bgcolor: '#fff', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <HeaderPublic />
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '50vh',
          flex: 1
        }}>
          <CircularProgress sx={{ color: '#1BE885' }} />
        </Box>
        <Footer />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ bgcolor: '#fff', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <HeaderPublic />
        <Container maxWidth="md" sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Alert severity="error">
            {error}
          </Alert>
        </Container>
        <Footer />
      </Box>
    );
  }

  if (!list) {
    return (
      <Box sx={{ bgcolor: '#fff', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <HeaderPublic />
        <Container maxWidth="md" sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Alert severity="warning">
            List not found
          </Alert>
        </Container>
        <Footer />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#fff', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <HeaderPublic  />
      
      <Container maxWidth="lg" sx={{ flex: 1, py: 4 }}>
        {/* List Header */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              color: '#333', 
              fontWeight: 400, 
              mb: 0,
              fontSize: { xs: '2rem', md: '3rem' }
            }}
          >
            {list.name}
          </Typography>
          
          {list.description && (
            <Typography 
              variant="h6" 
              sx={{ 
                color: '#1BE885', 
                fontWeight: 500,
                mb: 2,
                fontSize: { xs: '1rem', md: '1.25rem' }
              }}
            >
              {list.description}
            </Typography>
          )}
          
          <Chip 
            label={`${profiles.length} Profiles`}
            sx={{ 
              bgcolor: '#1BE885', 
              color: '#fff', 
              fontWeight: 600,
              fontSize: '0.9rem'
            }}
          />
        </Box>

        {/* Profiles Table */}
        {profiles.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" sx={{ color: '#666', mb: 2 }}>
              No profiles found in this list
            </Typography>
          </Box>
        ) : (
          <TableContainer 
            component={Paper} 
            sx={{ 
              borderRadius: 2, 
              boxShadow: 3,
              bgcolor: '#fff',
              border: '1px solid #e0e0e0'
            }}
          >
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 600, color: '#333' }}>S.No</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#333' }}>Picture</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#333' }}>Social Footprint</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#333' }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#333' }}>Position</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#333' }}>Company</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#333' }}>Location</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {profiles.map((profile, index) => (
                  <TableRow 
                    key={index} 
                    sx={{ 
                      '&:hover': { bgcolor: '#f9f9f9' },
                      '&:nth-of-type(odd)': { bgcolor: '#fafafa' }
                    }}
                  >
                    <TableCell sx={{ color: '#333' }}>{index + 1}</TableCell>
                    <TableCell>
                      {profile.error ? (
                        <Typography color="error">Not found</Typography>
                      ) : (
                        <img 
                          src={profile.profilePicture} 
                          alt="Profile" 
                          style={{ 
                            width: '50px', 
                            height: '50px', 
                            borderRadius: '50%',
                            objectFit: 'cover'
                          }} 
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {profile.error ? (
                        <Typography color="error">Not found</Typography>
                      ) : profile.linkedinLink ? (
                        <IconButton 
                          size="large" 
                          onClick={() => {
                            const url = profile.linkedinLink.startsWith('http') 
                              ? profile.linkedinLink 
                              : `https://${profile.linkedinLink}`;
                            window.open(url, '_blank');
                          }}
                          sx={{ 
                            '&:hover': { bgcolor: 'rgba(0, 119, 181, 0.1)' },
                            color: '#0077b5'
                          }}
                        >
                          <LinkedInIcon sx={{ fontSize: '25px' }} />
                        </IconButton>
                      ) : (
                        <Typography color="text.secondary">No link</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {profile.error ? (
                        <Typography color="error">Not found</Typography>
                      ) : (
                        <Typography 
                          sx={{ 
                            cursor: 'pointer', 
                            color: '#1976d2', 
                            textDecoration: 'underline',
                            fontWeight: 500,
                            '&:hover': { color: '#1565c0' }
                          }}
                          onClick={() => handleProfileClick(profile)}
                        >
                          {profile.fullName || 'N/A'}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {profile.error ? (
                        <Typography color="error">Not found</Typography>
                      ) : (
                        <Typography sx={{ color: '#666' }}>
                          {profile.currentPosition || 'N/A'}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {profile.error ? (
                        <Typography color="error">Not found</Typography>
                      ) : (
                        <Typography sx={{ color: '#666' }}>
                          {profile.company || 'N/A'}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {profile.error ? (
                        <Typography color="error">Not found</Typography>
                      ) : (
                        <Typography sx={{ color: '#666' }}>
                          {profile.location || 'N/A'}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Container>

      {/* Profile Detail Dialog */}
      <Dialog open={dialogOpen} onClose={handleDialogClose} fullWidth maxWidth={false} PaperProps={{sx: { width: '60vw', height: '80vh', marginTop: '64px', marginLeft: '240px', marginRight: '240px', borderRadius: 2, }}}>
        <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={(theme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon />
        </IconButton>
        <Box sx={{ width: '100%', height: '100%', p: 2 }}>
          {selectedProfile && (
            <ProfileView publicView={true} profileData={selectedProfile} />
          )}
        </Box>
      </Dialog>

      <Footer />

      {/* Email OTP Validation Dialog */}
      <EmailOTPValidation
        open={validationDialogOpen}
        onClose={handleValidationClose}
        onValidationSuccess={handleValidationSuccess}
      />
    </Box>
  );
};

export default PublicView;
